// tslint:disable: no-redundant-jsdoc
import { Observable } from 'rxjs';
import { IMitsTableGlobalFilters } from './mits-table-filter-option';
import { DataService } from 'src/packages/mitsBasics';
import { IDataService } from 'src/packages/mitsBasics';
import { BasicModel } from 'src/packages/mitsBasics';
import { MitsTableButton } from './mits-table-button';
import { IMitsTableColumn } from './mits-table-column';

export type SizeThreshold = 'sm' | 'md' | 'lg' | 'xl' | 'xxl';

export const sizeThresholdMappings = {
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200,
  xxl: 1450,
};

export interface IMitsTable<T extends BasicModel> {
  /**
   * Provider wich can be triggered to get the tables content
   */
  provider?: IDataService<T | any> | DataService<T | any>;
  /**
   * Should the provider be forced to use the backend directly without offline functions?
   * @default false
   */
  forceRest?: boolean;
  /**
   * Params wich get appended to the provided service
   * requires provider
   */
  params?: any;
  /**
   * Initiale Sortierung, die beim ersten Laden ans Backend gesendet wird
   * (Format wie order_by, z.B. 'sort_index ASC'). Ohne diese Angabe sortiert
   * die Tabelle initial nach 'remote_id DESC' bzw. 'id DESC'. Eine vom Nutzer
   * gewählte Spalten-Sortierung überschreibt diesen Wert weiterhin.
   */
  defaultOrderBy?: string;
  /**
   * Columns of this table
   */
  columns: IMitsTableColumn[];
  /**
   * When the table-width is smaller than this threshold, the shorten Version of the Title will be shown
   * @default 'md'
   */
  shortenTitleThreshold?: SizeThreshold;
  /**
   * Text wich will be displayed in the header
   */
  header?: string;
  /**
   * Should the search-button be shown?
   */
  hideSearch?: boolean;
  /**
   * Is the search-button enabled?
   */
  disableSearch?: boolean;
  /**
   * Name of the param to group all items by.
   */
  groupBy?: string;
  /**
   * Aktiviert einen Toolbar-Button "Gruppierung": beim Klick wählt der Nutzer per
   * Mehrfachauswahl, nach welchen Werten (groupBy-Feld) gruppiert wird. Die Liste
   * zeigt dann nur Datensätze der gewählten Werte (serverseitig gefiltert), gruppiert
   * mit Überschriften. Ohne Auswahl: keine Gruppierung. Ohne dieses Feld ist die
   * Gruppierung – sofern groupBy gesetzt ist – dauerhaft aktiv (ohne Button).
   * @requires groupBy
   */
  groupBySelect?: {
    /** Liefert die auswählbaren Werte (z.B. alle Artikelgruppen-Namen). */
    provider: () => Observable<string[]>;
    /** Backend-Param-Name, unter dem die gewählten Werte (base64/CSV) gesendet werden. */
    filterParam: string;
  };
  /**
   * If true will highlight the item
   */
  highlightIfTrue?: {
    /** Name of the param if true will highlight the item */
    names: string[];
    /**
     * Background in hex to highlight the item with
     * @default #db504a55 xxxxxx55 is suggested
     */
    bgColor?: string;
  };
  /**
   * If value equal table will disable the item
   */
  disableIf?: {
    /** Name of the param if true will disable the item */
    name: string;
    /** Value of the param if true will disable the item */
    values: any[];
  };
  /**
   * Deaktiviert das nachladen bei einem reload event
   */
  disableReload?: boolean;
  /**
   * Time in milliseconds after last input to call the backend
   * @default 400 ms
   */
  searchDebounceTime?: number;
  /**
   * Should the filters-button be shown?
   */
  hideFilters?: boolean;
  /**
   * Is the filters-button enabled?
   */
  disableFilters?: boolean;
  /**
   * Should filters be applied after the popover gets closed?
   * @default false the filters will get applied instantly after changes
   */
  applyFiltersAfterClose?: boolean;
  /**
   * Should the add-button be hidden?
   */
  hideAdd?: boolean;
  /**
   * Is the add-button enabled?
   */
  disableAdd?: boolean;
  /**
   * Switch add-text / add-icon when this threshold gets passed
   * @example 'sm' or 'lg' or 'xxl'
   * default 'md'
   */
  addTextThreshold?: SizeThreshold;
  /**
   * Disables the logic to click on the individual Items/Rows
   * @default false
   */
  disableItemClick?: boolean;
  /**
   * Disables the hover effect on the individual Items/Rows
   * @default false
   */
  disableHoverEffect?: boolean;
  /**
   * Enables Buttons inside each row at the end to manage them (Delete)
   * @default false
   */
  enableInlineEditing?: {
    /** Enable the inline delete button/feature */
    delete?: {
      /**
       * Uses "_destroy"-Tag on deleted rows and doesn't delete the item in that array
       * @default false Deletes item in array
       * @overrides Provider will be ignored / U can work with the Output "afterNestedDestroy" to save your Parent Element
       */
      useNestedDestroy?: boolean;
    };
  };
  /**
   * Switch inline action-buttons / dots-menu when this threshold gets passed
   * @example 'sm' or 'lg' or 'xxl' / 'none' deactivates the deots-menu
   * @default 'sm'
   */
  actionsMenuThreshold?: SizeThreshold | 'none';
  /**
   * Static width in PIXELS of the action-buttons column
   * @default null no static width / auto by HTML-Table
   */
  actionsMenuColumnWidth?: number;
  /**
   * Additional custom action-buttons
   */
  actionsMenuButtons?: MitsTableButton<T>[];
  /**
   * Disable the content wrap of each column into a second row? This will cut the content of
   * @default false Content will be wrapped
   * @overrides "columns" value of "disableContentWrap"
   * @overrides "setTableLayoutFixed" to "true"
   */
  disableContentWrap?: boolean;
  /**
   * As this Threshold gets passed the disableContentWrap will be forced to "false"
   * @default 'md'
   * @overrides "disableContentWrap" to "false" if threshold gets passed
   */
  disableContentWrapThreshold?: SizeThreshold;
  /**
   * Sets the width of the Table to be fixed so no sideways scrolling will be possible
   * @default false Table will get bigger than 100%
   */
  setTableLayoutFixed?: boolean;
  /**
   * Resets the padding of the header title so only the card padding defines the outer distance
   * @default false
   */
  cardDesign?: boolean;
  /**
   * Disables the double row height on mobile/touch devices
   * @default false
   */
  disableTouchResizing?: boolean;
  /**
   * Optional global Filters for the MitsTable Component to filter by specified attributes on first level
   * INFO: This only works in online mode (forceRest) and if the specified filter-functionality is provided by backend controller
   */
  globalFilter?: IMitsTableGlobalFilters<T>;
}
